import {
  CheckCircleFilled,
  CloseOutlined,
  DownOutlined
} from "@ant-design/icons"
import { Button, Dropdown, Grid, Tooltip, Typography } from "antd"
import type { MenuProps } from "antd"
import React, {
  type ReactNode,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from "react"
import { useTranslation } from "react-i18next"

import { cn } from "../../utils/cn.ts"
import { TABLE_BULK_BAR_CLASS } from "./table-bulk-bar.ts"

const { Text } = Typography

export interface BulkBarAction {
  key: string
  /** Translated, and the button's accessible name. */
  label: string
  icon?: ReactNode
  onClick: () => void
  disabled?: boolean
  /** Shown as a tooltip (or under the menu entry) while `disabled`. */
  disabledReason?: string
  loading?: boolean
}

export interface BulkActionBarProps {
  /** The toolbar shows only while this is above zero. */
  selectedCount: number
  /** Translated and pluralised by the screen: "2 projects selected". */
  selectedLabel: string
  /** The toolbar's accessible name. */
  ariaLabel: string
  /**
   * Common actions in priority order. As many as the width allows stay on the
   * bar; the rest move into More. Leave out the ones the user may not run.
   */
  actions?: BulkBarAction[]
  /** Separated at the right, in red text, and moved into More on phones. */
  destructiveAction?: BulkBarAction
  /** Offered only when more eligible results exist than are selected. */
  selectAll?: { label: string; onClick: () => void }
  /** A short warning beside the count, e.g. a selection over the bulk limit. */
  notice?: ReactNode
  onClear: () => void
  /** Outcome reporting that outlives the selection, rendered under the bar. */
  children?: ReactNode
  /** Keeps the wrapper mounted for `children` once the selection is gone. */
  visible?: boolean
  /** Positioning of the wrapper; defaults to the table-card bottom pin. */
  positionClassName?: string
  /** What the bar floats over: a list's card (default) or the bare page. */
  surface?: "card" | "page"
  "data-testid"?: string
}

const MAX_ACTIONS_ON_BAR = 3

interface Fit {
  actionsOnBar: number
  clearLabel: boolean
  destructiveInMenu: boolean
}

/**
 * The layouts the bar steps through, widest first, until it stops
 * overflowing: common actions go into More one at a time, then Clear
 * selection drops its label, then the destructive action joins More.
 */
const fitLadder = (
  actionCount: number,
  hasDestructive: boolean,
  phone: boolean
): Fit[] => {
  const ladder: Fit[] = []
  const most = Math.min(actionCount, MAX_ACTIONS_ON_BAR)
  for (let shown = most; shown >= Math.min(most, 1); shown--)
    ladder.push({ actionsOnBar: shown, clearLabel: !phone, destructiveInMenu: phone })
  const fewest = Math.min(most, 1)
  if (!phone)
    ladder.push({ actionsOnBar: fewest, clearLabel: false, destructiveInMenu: false })
  if (hasDestructive && !phone)
    ladder.push({ actionsOnBar: fewest, clearLabel: false, destructiveInMenu: true })
  if (fewest)
    ladder.push({ actionsOnBar: 0, clearLabel: false, destructiveInMenu: hasDestructive })
  return ladder
}

// The fixed support launcher parks in the viewport's bottom-right corner and
// widens to this much when it opens.
const LAUNCHER_WIDTH = 64

/**
 * Whether the bar reaches the viewport's right edge, where the support
 * launcher would sit over its last control. A list in a narrower column — a
 * project's Milestones beside its indicators — keeps the room for actions.
 */
const useReachesLauncher = (mounted: boolean) => {
  const ref = useRef<HTMLDivElement>(null)
  const [reaches, setReaches] = useState(true)

  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const check = () =>
      setReaches(
        el.getBoundingClientRect().right > window.innerWidth - LAUNCHER_WIDTH
      )
    check()
    window.addEventListener("resize", check)
    const observer =
      typeof ResizeObserver === "undefined" ? null : new ResizeObserver(check)
    observer?.observe(el)
    return () => {
      window.removeEventListener("resize", check)
      observer?.disconnect()
    }
  }, [mounted])

  return { ref, reaches }
}

/**
 * Picks the widest layout that fits the bar's own width — not the viewport's,
 * since the sidebar and the card around a list decide how much of it the bar
 * gets. Whenever the width or what the bar holds changes, it starts again from
 * the widest layout and steps down one rung per render, before paint, while
 * the toolbar overflows. Nothing else re-measures, so typing in a dialog the
 * screen keeps beside the bar forces no layout.
 */
const useFit = (ladder: Fit[], contentKey: string, mounted: boolean) => {
  const ref = useRef<HTMLDivElement>(null)
  const [rung, setRung] = useState(0)
  const [width, setWidth] = useState(0)
  const last = ladder.length - 1
  const fitKey = `${contentKey}|${width}|${mounted}`
  const fittedFor = useRef(fitKey)

  useLayoutEffect(() => {
    if (fittedFor.current !== fitKey) {
      fittedFor.current = fitKey
      setRung(0)
      return
    }
    const el = ref.current
    if (el && rung < last && el.scrollWidth > el.clientWidth + 1)
      setRung((current) => Math.min(current + 1, last))
  }, [fitKey, rung, last])

  useEffect(() => {
    const el = ref.current
    if (!el || typeof ResizeObserver === "undefined") return
    const observer = new ResizeObserver(() => setWidth(el.clientWidth))
    observer.observe(el)
    return () => observer.disconnect()
  }, [mounted])

  return { ref, fit: ladder[Math.min(rung, last)] }
}

const Divider = () => (
  <span aria-hidden className="hidden h-6 w-px shrink-0 bg-[#E5E7EB] md:block" />
)

const ActionButton = ({
  action,
  destructive = false
}: {
  action: BulkBarAction
  destructive?: boolean
}) => {
  const button = (
    <Button
      type={destructive ? "text" : "default"}
      danger={destructive}
      icon={action.icon}
      disabled={action.disabled}
      loading={action.loading}
      onClick={action.onClick}
      className="shrink-0"
    >
      {action.label}
    </Button>
  )

  if (!action.disabled || !action.disabledReason) return button

  // React never fires a disabled button's mouseenter, so the tooltip hangs
  // off this span.
  return (
    <Tooltip title={action.disabledReason}>
      <span className="inline-flex">{button}</span>
    </Tooltip>
  )
}

const toMenuItem = (
  action: BulkBarAction,
  danger = false
): NonNullable<MenuProps["items"]>[number] => ({
  key: action.key,
  icon: action.icon,
  danger,
  disabled: action.disabled || action.loading,
  onClick: action.onClick,
  label:
    action.disabled && action.disabledReason ? (
      <span className="flex flex-col">
        <span>{action.label}</span>
        <span className="text-xs text-[#6B7280]">{action.disabledReason}</span>
      </span>
    ) : (
      action.label
    )
})

/**
 * The one bulk-selection toolbar for every multi-select list: a compact white
 * bar pinned to the bottom of the list's card. Screens own their actions,
 * permissions and dialogs and hand them in as configuration — nothing here
 * knows which list it is on.
 *
 * It sits in the flow (sticky, not fixed), so the list above it gives up the
 * bar's height rather than scrolling its last row and pagination underneath.
 */
export function BulkActionBar({
  selectedCount,
  selectedLabel,
  ariaLabel,
  actions = [],
  destructiveAction,
  selectAll,
  notice,
  onClear,
  children,
  visible,
  positionClassName = TABLE_BULK_BAR_CLASS,
  surface = "card",
  "data-testid": testId
}: BulkActionBarProps) {
  const { t } = useTranslation()
  const screens = Grid.useBreakpoint()
  // Before the first measurement every breakpoint reads undefined; treat that
  // as desktop so the bar does not flash its phone layout.
  const phone = screens.md === false
  const hasDestructive = !!destructiveAction
  const ladder = useMemo(
    () => fitLadder(actions.length, hasDestructive, phone),
    [actions.length, hasDestructive, phone]
  )
  const contentKey = [
    selectedLabel,
    selectAll?.label,
    ...actions.map((action) => action.label),
    destructiveAction?.label,
    phone
  ].join("\u0000")
  const { ref: toolbarRef, fit } = useFit(
    ladder,
    contentKey,
    selectedCount > 0
  )
  const { ref: wrapperRef, reaches } = useReachesLauncher(
    selectedCount > 0 || !!visible
  )
  const inset = reaches ? "pl-2 pr-14 md:pl-4 md:pr-16" : "px-2 md:px-4"

  if (!selectedCount && !visible) return null

  const shown = actions.slice(0, fit.actionsOnBar)
  const overflow = actions.slice(fit.actionsOnBar)
  const destructiveInMenu = fit.destructiveInMenu && destructiveAction

  const menuItems: MenuProps["items"] = [
    ...overflow.map((action) => toMenuItem(action)),
    ...(destructiveInMenu
      ? [
          ...(overflow.length ? [{ type: "divider" as const }] : []),
          toMenuItem(destructiveAction, true)
        ]
      : [])
  ]

  return (
    <div ref={wrapperRef} className={positionClassName} data-testid={testId}>
      {selectedCount ? (
        // Inset from the card's edges so it reads as floating. The strip itself
        // is opaque because on a list that scrolls with the page, rows pass
        // under the bar.
        <div
          className={cn(
            "pb-3 pt-2",
            surface === "card"
              ? "bg-[var(--ant-color-bg-container,#fff)]"
              : "bg-background",
            inset
          )}
        >
          <div
            ref={toolbarRef}
            role="toolbar"
            aria-label={ariaLabel}
            className="flex min-h-14 items-center overflow-hidden gap-2 rounded-xl border border-[#E5E7EB] bg-white px-3 py-2 shadow-[0_4px_16px_rgba(16,24,40,0.08)] md:gap-3 md:px-4"
          >
            {/* Decorative; a phone's bar needs the room for its controls. */}
            <CheckCircleFilled
              aria-hidden
              className="shrink-0 text-xl text-primary max-sm:!hidden"
            />
            <span aria-live="polite" aria-atomic="true">
              <Text strong className="whitespace-nowrap">
                {selectedLabel}
              </Text>
            </span>

            {selectAll ? (
              <Button
                type="link"
                className="shrink-0 !px-1"
                onClick={selectAll.onClick}
              >
                {selectAll.label}
              </Button>
            ) : null}

            {shown.length || menuItems.length ? <Divider /> : null}

            <div className="flex shrink-0 items-center gap-2">
              {shown.map((action) => (
                <ActionButton key={action.key} action={action} />
              ))}

              {menuItems.length ? (
                <Dropdown menu={{ items: menuItems }} trigger={["click"]}>
                  <Button className="shrink-0">
                    {t("bulkActionBar.more")} <DownOutlined aria-hidden />
                  </Button>
                </Dropdown>
              ) : null}
            </div>

            <span className="flex-1" />

            {destructiveAction && !destructiveInMenu ? (
              <>
                <Divider />
                <ActionButton action={destructiveAction} destructive />
              </>
            ) : null}

            <Divider />
            <Tooltip
              title={fit.clearLabel ? undefined : t("bulkActionBar.clearSelection")}
            >
              <Button
                type="text"
                className="shrink-0 max-md:!h-11 max-md:!w-11"
                icon={<CloseOutlined aria-hidden />}
                aria-label={t("bulkActionBar.clearSelection")}
                onClick={onClear}
              >
                {fit.clearLabel ? t("bulkActionBar.clearSelection") : null}
              </Button>
            </Tooltip>
          </div>
          {/* Under the bar rather than in it, so it shows at every width and a
              disabled action's reason never depends on hovering it. */}
          {notice ? (
            <Text type="warning" className="mt-1 block px-1 text-sm">
              {notice}
            </Text>
          ) : null}
        </div>
      ) : null}

      {children ? (
        <div className={cn(inset, { "pt-2": !selectedCount })}>
          {children}
        </div>
      ) : null}
    </div>
  )
}
